import { LoadingButton } from '@mui/lab';
import { Box, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PORTAL_LISTING_API_URL } from 'api/apiConstants';
import axios from 'axios';
import PartialPageSpinner from 'components/PartialPageSpinner';
import { Key, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OauthPopup from 'react-oauth-popup';
import { setSession } from 'utils/jwt';
import Page from '../../components/Page';
import SuccessManager from '../user/Portal/SuccessManager';

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

interface PortalType {
  _id: Key | null | undefined;
  hub_domain: string;
  portal_id: number;
}

export default function PortalListingOnboarding({
  portalConnecting,
  connectToPortal,
  redirectUrl,
  onCode,
  portalAlreadyConnected,
  userToken,
  nextStep,
  setUserToken,
}: {
  portalConnecting: boolean;
  connectToPortal: () => void;
  nextStep: VoidFunction;
  redirectUrl: string | null;
  portalAlreadyConnected: boolean | null;
  userToken: null | string;
  onCode: (code: string, params: any) => void;
  setUserToken: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [portalData, setPortalData] = useState<null | PortalType[]>();
  const oauthRef = useRef<HTMLElement>(null);

  const onClose = () => {};

  useEffect(() => {
    if (redirectUrl && oauthRef.current) {
      oauthRef.current.click();
    }
  }, [redirectUrl]);

  useEffect(() => {
    if (userToken !== null) {
      setIsLoading(true);
      axios
        .get(`${PORTAL_LISTING_API_URL}`, {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        })
        .then(resp => {
          setPortalData(resp.data.data);
          setIsLoading(false);
        });
    }
  }, []);

  const moveToFormsPage = () => {
    nextStep();
    setUserToken(userToken);
    setSession(userToken);
  };

  return (
    <Page title={t('portal_listing')}>
      <RootStyle>
        <div className='flex w-full mx-auto justify-center mt-[4rem] px-[2%] 2xl:px-[10%] flex-col'>
          <div className='flex flex-col px-10'>
            <Stack direction='column' alignItems='center'>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant='h4' gutterBottom>
                  {t('connect_portals')}
                </Typography>
                <Typography
                  sx={{ color: 'text.secondary' }}
                  className='text-sm'
                >
                  {t('choose_listed_portals')}
                </Typography>
              </Box>
            </Stack>
            {redirectUrl && (
              <OauthPopup
                url={redirectUrl}
                title={t('connect_hubspot')}
                height={800}
                width={800}
                onCode={onCode}
                onClose={onClose}
              >
                <span ref={oauthRef}></span>
              </OauthPopup>
            )}
            <LoadingButton
              loading={portalConnecting}
              color='info'
              variant='contained'
              sx={{ width: '220px' }}
              onClick={connectToPortal}
              className='max-h-[2.2rem] mt-10 mb-2'
            >
              + {t('add_new_portal')}
            </LoadingButton>
            {portalAlreadyConnected === true && (
              <div className='text-red-500 text-sm'>
                {t('portal_already_in_use')}
              </div>
            )}
          </div>

          {userToken !== null ? (
            <>
              {isLoading ? (
                <>
                  <div>
                    <PartialPageSpinner />
                  </div>
                </>
              ) : (
                <>
                  {portalData?.map((portal: PortalType) => (
                    <ListItem
                      sx={{
                        marginBottom: '15px',
                        display: 'flex',
                        padding: '20px 10px',
                      }}
                      button
                      key={portal._id}
                      className='shadow-md rounded-md shadow-slate-100 mt-5'
                    >
                      <ListItemText
                        onClick={moveToFormsPage}
                        primary={portal.hub_domain}
                        secondary={
                          <>
                            <Typography
                              sx={{ display: 'inline' }}
                              component='span'
                              variant='body2'
                              color='text.secondary'
                            >
                              {t('id')} {portal.portal_id}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
        <SuccessManager />
      </RootStyle>
    </Page>
  );
}
